import { ErrorBoundary } from '@sentry/react'
import React, { Suspense } from 'react'
import { LuX } from 'react-icons/lu'

import AdditionalConsentFiles, {
  AdditionalClientConsentFile,
} from '@/components/misc/ClientIntroductionAdditionalPdfs/AdditionalConsentFiles'
import CompanyProfileSkeleton from '@/components/misc/CompanyProfile/components/CompanyProfileSkeleton'
import FirmDetailsNotFound from '@/components/misc/FirmDetailsNotFound'
import ReferralESignaturePreview from '@/components/misc/ReferralESignaturePreview'
import { ServiceFeeDetail } from '@/components/misc/ServiceFeesTable/ServiceFeesTableRows'
import {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer'
import { REFERRAL_CONSENT_STAGE } from '@/lib/constants'
import { CommercialAgreementTypeOption } from '@/redux/slices/commercialAgreementsForm'
import {
  CommercialAgreementStatus,
  ReferralConsentLetterVariant,
  ReferralType,
} from '@/types/misc'

export type ESignatureLetterModalProps = {
  isShowing: boolean
  onHide: () => void
  fromCompanyId: number
  toCompanyId: number
  title: string
  agreementType?: CommercialAgreementTypeOption
  agreementStatus: CommercialAgreementStatus
  serviceFees: ServiceFeeDetail[]
  renderHeader?: (props: RenderHeaderProps) => React.ReactNode
  commissionJustification?: string
  onEditCommissionJustification?: (commissionJustification: string) => void
  serviceAreaIds?: number[]
  referralType?: ReferralType
  clientId?: number
  editableConsentFiles?: AdditionalClientConsentFile[]
  onAddConsentFile?: (file: AdditionalClientConsentFile) => void
  onRemoveConsentFile?: (consentFileToken: string) => void
  onMoveConsentFileUp?: (consentFileToken: string) => void
  onMoveConsentFileDown?: (consentFileToken: string) => void
  consentLetterVariant: ReferralConsentLetterVariant
}

interface RenderHeaderProps {
  commissionJustification?: string
}

const ESignatureLetterModal: React.FC<ESignatureLetterModalProps> = ({
  isShowing,
  onHide,
  fromCompanyId,
  toCompanyId,
  serviceFees,
  title,
  renderHeader,
  agreementType,
  agreementStatus,
  commissionJustification,
  onEditCommissionJustification,
  serviceAreaIds = [],
  referralType,
  clientId,
  editableConsentFiles,
  onAddConsentFile,
  onRemoveConsentFile,
  onMoveConsentFileUp,
  onMoveConsentFileDown,
  consentLetterVariant,
}) => {
  const canConfigureAdditionalPdfs = !!(
    editableConsentFiles &&
    onAddConsentFile &&
    onRemoveConsentFile &&
    onMoveConsentFileUp &&
    onMoveConsentFileDown
  )

  return (
    <Drawer open={isShowing} direction="right" onHide={onHide}>
      <DrawerContent className="md:w-230">
        <DrawerHeader
          className="flex flex-row items-center justify-between gap-4"
        >
          <DrawerTitle style={{ marginLeft: '5mm' }}>{title}</DrawerTitle>{' '}
          <DrawerClose asChild>
            <LuX className="text-xl" />
          </DrawerClose>
        </DrawerHeader>

        <DrawerBody className="pt-0 pb-12">
          <ErrorBoundary fallback={<FirmDetailsNotFound className="px-6" />}>
            <Suspense fallback={<CompanyProfileSkeleton />}>
              <ReferralESignaturePreview
                clientId={clientId}
                fromCompanyId={fromCompanyId}
                toCompanyId={toCompanyId}
                serviceFees={serviceFees}
                agreementType={agreementType}
                commissionJustification={commissionJustification}
                onEditCommissionJustification={onEditCommissionJustification}
                showAdditionalPdfPages={!canConfigureAdditionalPdfs}
                serviceAreaIds={serviceAreaIds}
                referralType={referralType}
                consentStage={REFERRAL_CONSENT_STAGE.INTRODUCTION}
                consentLetterVariant={consentLetterVariant}
                agreementStatus={agreementStatus}
                render={({
                  renderPreview,
                  commissionJustification: newCommissionJustification,
                }) => (
                  <>
                    <section
                      style={{
                        margin: '10px 5mm 0', // 5mm matches the horizontal margin set by the PDF preview
                      }}
                    >
                      {renderHeader &&
                        renderHeader({
                          commissionJustification: newCommissionJustification,
                        })}
                    </section>

                    <>{renderPreview()}</>
                  </>
                )}
              />

              {canConfigureAdditionalPdfs && (
                <AdditionalConsentFiles
                  consentFiles={editableConsentFiles}
                  onAddConsentFile={onAddConsentFile}
                  onRemoveConsentFile={onRemoveConsentFile}
                  onMoveConsentFileUp={onMoveConsentFileUp}
                  onMoveConsentFileDown={onMoveConsentFileDown}
                />
              )}
            </Suspense>
          </ErrorBoundary>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  )
}

export default ESignatureLetterModal
